import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronRight, Phone, Mail, MapPin, Building2, Wrench, Home, Truck, Palette, Users, FileText, Shield, Settings, X, CheckCircle } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import Award from './../assets/awars.png';
import heroImage from "../assets/Civil-Engineering.png";

const Homepage = () => {
  const navigate = useNavigate();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [quoteStatus, setQuoteStatus] = useState(''); // '', 'sending', 'success', 'error'
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    // Auto-rotate services
    const interval = setInterval(() => {
      setActiveService((prev) => (prev + 1) % services.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setQuoteStatus('sending');

    try {
      const response = await fetch('https://gusgroupltd.com/api/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: 'quote',
          website: '' // Honeypot field
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setQuoteStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        
        // Auto-close modal after 3 seconds
        setTimeout(() => {
          setShowQuoteModal(false);
          setQuoteStatus('');
        }, 3000);
      } else {
        setQuoteStatus('error');
        setTimeout(() => {
          setQuoteStatus('');
        }, 5000);
      }
    } catch (error) {
      console.error('Error:', error);
      setQuoteStatus('error');
      setTimeout(() => {
        setQuoteStatus('');
      }, 5000);
    }
  };

  const services = [
    {
      icon: <Wrench className="w-8 h-8" />,
      title: "Remodeling & Renovations",
      description: "Residential and commercial property upgrades."
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Road Construction & Civil Works",
      description: "Infrastructure and highway development."
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Real Estate Services",
      description: "Realtor activities, property management, and brokerage."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Furniture Supply & Design",
      description: "Custom and ready-made furniture solutions."
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Interior & Exterior Decoration",
      description: "Modern design, painting, and finishing services."
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: "Building Development & Construction",
      description: "Full-scale property development projects."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Importation & Exportation",
      description: "International trade facilitation."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Sales & Purchase of Properties",
      description: "Land, residential, and commercial property transactions."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Consultancy Services",
      description: "Advisory in real estate, construction, and business development."
    },
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Facility Management & Maintenance",
      description: "Long-term upkeep of buildings and infrastructure."
    }
  ];

  const stats = [
    { number: "500+", label: "Projects Completed" },
    { number: "15+", label: "Years Experience" },
    { number: "200+", label: "Happy Clients" },
    { number: "50+", label: "Expert Team" }
  ];

  const testimonials = [
    {
      name: "Kwame Mensah",
      role: "Property Developer",
      content: "GUS Group transformed our vision into reality. Their attention to detail and professionalism is unmatched.",
      rating: 5
    },
    {
      name: "Ama Serwaa",
      role: "Homeowner",
      content: "From design to completion, the team delivered excellence at every stage. Highly recommended!",
      rating: 5
    },
    {
      name: "John Agyeman",
      role: "Business Owner",
      content: "Outstanding work on our commercial property. They completed the project on time and within budget.",
      rating: 5
    }
  ];

  const portfolioProjects = [
    {
      title: "Luxury Residential Complex",
      category: "Residential",
      description: "Modern 50-unit apartment complex in Accra",
      image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&h=600&fit=crop"
    },
    {
      title: "Corporate Office Building",
      category: "Commercial",
      description: "10-story commercial building in Airport City",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=600&fit=crop"
    },
    {
      title: "Highway Construction",
      category: "Infrastructure",
      description: "15km highway development project",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&h=600&fit=crop"
    },
    {
      title: "Shopping Mall Renovation",
      category: "Remodeling",
      description: "Complete renovation of Accra Mall",
      image: "https://images.unsplash.com/photo-1555529902-5261145633bf?w=800&h=600&fit=crop"
    },
    {
      title: "Villa Interior Design",
      category: "Interior",
      description: "Luxury villa interior and exterior decoration",
      image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&h=600&fit=crop"
    },
    {
      title: "Industrial Warehouse",
      category: "Construction",
      description: "50,000 sq ft warehouse facility",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop"
    }
  ];

  return (
    <div className="bg-black text-white font-sans">
      <Navigation onQuoteClick={() => setShowQuoteModal(true)} />

      {/* Hero Section - Version with Construction Background */}
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
       <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/50"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <div>
          <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
            Premium Construction & Real Estate
          </h2>
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Building Dreams,
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
              Creating Futures
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            Ghana's premier construction company delivering excellence in building development,
            renovations, and comprehensive real estate solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button 
              onClick={() => setShowQuoteModal(true)}
              className="bg-yellow-400 text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center gap-2 shadow-xl"
            >
              Start Your Project
              <ArrowRight className="w-5 h-5" />
            </button>
            <button 
              onClick={() => navigate('/portfolio')}
              className="border-2 border-yellow-400 text-yellow-400 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-400 hover:text-black transition-all"
            >
              View Portfolio
            </button>
          </div>
        </div>

        {/* Floating Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              className="bg-black/60 backdrop-blur-md rounded-2xl p-6 border border-yellow-400/30 hover:border-yellow-400/50 transition-all transform hover:scale-105"
            >
              <h3 className="text-4xl font-bold text-yellow-400 mb-2">{stat.number}</h3>
              <p className="text-gray-300">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronRight className="w-6 h-6 text-yellow-400 rotate-90" />
      </div>
    </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Services
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Comprehensive Construction Solutions
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              From concept to completion, we deliver exceptional results across all facets of construction and real estate.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/10 hover:border-yellow-400/50 transition-all duration-300 transform hover:scale-105 cursor-pointer"
                onMouseEnter={() => setActiveService(index)}
              >
                <div className="w-16 h-16 bg-yellow-400/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-yellow-400 transition-all">
                  <div className="text-yellow-400 group-hover:text-black transition-colors">
                    {service.icon}
                  </div>
                </div>
                <h4 className="text-2xl font-bold mb-3 text-white group-hover:text-yellow-400 transition-colors">
                  {service.title}
                </h4>
                <p className="text-gray-400 mb-6">
                  {service.description}
                </p>
                <button 
                  onClick={() => {
                    setFormData({ ...formData, service: service.title });
                    setShowQuoteModal(true);
                  }}
                  className="text-yellow-400 font-semibold flex items-center gap-2 group-hover:gap-4 transition-all"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/services')}
              className="bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section id="about" className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
                About GUS Group
              </h2>
              <h3 className="text-4xl md:text-5xl font-bold mb-6">
                15+ Years of Excellence in Construction
              </h3>
              <p className="text-gray-300 text-lg mb-6">
                GUS Group Ltd stands as Ghana's premier construction and real estate company, 
                delivering world-class solutions that transform visions into reality.
              </p>
              <p className="text-gray-400 mb-8">
                With over 15 years of industry leadership, we combine innovative design, 
                superior craftsmanship, and unwavering commitment to excellence.
              </p>
              <button 
                onClick={() => navigate('/about')}
                className="bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center gap-2"
              >
                Learn More About Us
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="relative">
              <div className="aspect-square bg-gradient-to-br from-yellow-400/20 to-transparent rounded-3xl border border-yellow-400/30 flex items-center justify-center">
                {/*<Building2 className="w-32 h-32 text-yellow-400/30" />*/}
                <img src={ Award } className="rounded-3xl"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Client Testimonials
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              What Our Clients Say
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div className="border-t border-gray-800 pt-4">
                  <h4 className="text-white font-bold">{testimonial.name}</h4>
                  <p className="text-yellow-400 text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Preview */}
      <section id="portfolio" className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Our Portfolio
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Recent Projects
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Explore our showcase of completed projects across Ghana
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.map((project, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-yellow-400/20 hover:border-yellow-400/50 transition-all cursor-pointer"
                onClick={() => navigate('/portfolio')}
              >
                {/* Project Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="bg-yellow-400 text-black px-3 py-1 rounded-full text-sm font-semibold">
                      {project.category}
                    </span>
                  </div>

                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                    <div className="text-center">
                      <ArrowRight className="w-12 h-12 text-yellow-400 mx-auto mb-2" />
                      <p className="text-white font-semibold">View Details</p>
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div className="p-6 bg-gradient-to-br from-gray-900 to-black">
                  <span className="text-yellow-400 text-sm font-semibold">{project.category}</span>
                  <h4 className="text-white text-xl font-bold mt-2 mb-2">{project.title}</h4>
                  <p className="text-gray-400 text-sm">{project.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/portfolio')}
              className="bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto"
            >
              View All Projects
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Contact Preview */}
      <section id="contact" className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-yellow-400 text-sm uppercase tracking-widest mb-4 font-semibold">
              Get In Touch
            </h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6">
              Contact Us
            </h3>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Ready to start your project? Reach out to us today
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-2">Office Location</h4>
                  <p className="text-gray-400">Opposite HGL Guest House<br />Nii Afutu Tsuru St, Accra</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-2">Phone</h4>
                  <a href="tel:0592668258" className="text-gray-400 hover:text-yellow-400 transition-colors">
                    0592668258
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-yellow-400/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-white font-bold mb-2">Email</h4>
                  <a href="mailto:info@gusgroupltd.com" className="text-gray-400 hover:text-yellow-400 transition-colors">
                    info@gusgroupltd.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button 
              onClick={() => navigate('/contact')}
              className="bg-yellow-400 text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center gap-2 mx-auto"
            >
              Visit Contact Page
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-r from-yellow-400 to-yellow-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Let's bring your vision to life. Contact us today for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setShowQuoteModal(true)}
              className="bg-black text-yellow-400 px-8 py-4 rounded-full font-bold text-lg hover:bg-gray-900 transition-all"
            >
              Get Free Quote
            </button>
            <a  href="tel:0592668258"
              className="border-2 border-black text-black px-8 py-4 rounded-full font-bold text-lg hover:bg-black hover:text-yellow-400 transition-all inline-block"
            >
              Call: 0592668258
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-400/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-3xl font-bold text-white">Request a Quote</h3>
                <button
                  onClick={() => {
                    setShowQuoteModal(false);
                    setQuoteStatus('');
                  }}
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-yellow-400 hover:text-black transition-all"
                  disabled={quoteStatus === 'sending'}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Status Messages */}
              {quoteStatus === 'sending' && (
                <div className="mb-6 bg-blue-500/10 border border-blue-500/50 rounded-xl p-4 flex items-start gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <div>
                    <h4 className="text-blue-500 font-bold mb-1">Sending Request...</h4>
                    <p className="text-gray-300 text-sm">Please wait while we process your quote request.</p>
                  </div>
                </div>
              )}

              {quoteStatus === 'success' && (
                <div className="mb-6 bg-green-500/10 border border-green-500/50 rounded-xl p-4 flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-green-500 font-bold mb-1">Quote Request Sent Successfully!</h4>
                    <p className="text-gray-300 text-sm">Thank you! We'll contact you soon with your quote. This window will close automatically.</p>
                  </div>
                </div>
              )}

              {quoteStatus === 'error' && (
                <div className="mb-6 bg-red-500/10 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
                  <X className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-red-500 font-bold mb-1">Failed to Send Request</h4>
                    <p className="text-gray-300 text-sm">Please try again or call us directly at 0592668258</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      disabled={quoteStatus === 'sending'}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      disabled={quoteStatus === 'sending'}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="0501234567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Service Required *</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="">Select a service</option>
                    {services.map((service, i) => (
                      <option key={i} value={service.title}>{service.title}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">Project Details *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    disabled={quoteStatus === 'sending'}
                    rows="5"
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-yellow-400 focus:outline-none transition-all resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tell us about your project requirements, timeline, budget, etc."
                  ></textarea>
                </div>

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowQuoteModal(false);
                      setQuoteStatus('');
                    }}
                    disabled={quoteStatus === 'sending'}
                    className="flex-1 border-2 border-gray-700 text-white px-8 py-4 rounded-full font-bold hover:border-yellow-400 hover:text-yellow-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={quoteStatus === 'sending' || quoteStatus === 'success'}
                    className="flex-1 bg-yellow-400 text-black px-8 py-4 rounded-full font-bold hover:bg-yellow-500 transition-all transform hover:scale-105 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {quoteStatus === 'sending' ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Request
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}


      {/* WhatsApp Floating Button */}
      <a  href="https://wa.me/233592668258?text=Hello%20GUS%20Group,%20I'm%20interested%20in%20your%20services"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all transform hover:scale-110 z-40"
      >
        <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
      </a>
    </div>
  );
};

export default Homepage;